import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import axios from '../utils/api';

const CourierReturnPage = () => {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [storeSettings, setStoreSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupNote, setPickupNote] = useState('');
  const [itemChecks, setItemChecks] = useState({});

  const toDateTimeLocalValue = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const formatIndianAmount = (value) => new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

  const formatISTDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'medium',
      hour12: true,
    }).format(parsed);
  };

  const returnItems = useMemo(() => (order?.items || []).filter((item) => item.status === 'return_requested'), [order]);

  useEffect(() => {
    const fetchCourierData = async () => {
      try {
        setLoading(true);
        setError('');

        const orderResponse = await axios.get(`/api/orders/courier-access/${token}`);
        setOrder(orderResponse.data);
        setStoreSettings(null);
        setPickupDate(toDateTimeLocalValue(new Date().toISOString()));

        const initialChecks = {};
        (orderResponse.data?.items || []).forEach((item) => {
          if (item.status === 'return_requested') {
            initialChecks[item.id] = false;
          }
        });
        setItemChecks(initialChecks);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load courier return details');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchCourierData();
    }
  }, [token]);

  const handlePrintReturnSlip = () => {
    if (!order) return;

    const returnSlipWindow = window.open('', '_blank');
    if (!returnSlipWindow) {
      setError('Allow pop-ups to print the return slip.');
      return;
    }

    const storeAddressLines = [];
    if (storeSettings?.store_name) storeAddressLines.push(storeSettings.store_name);
    if (storeSettings?.store_addr1) storeAddressLines.push(storeSettings.store_addr1);
    if (storeSettings?.store_addr2) storeAddressLines.push(storeSettings.store_addr2);
    const cityState = [storeSettings?.store_city, storeSettings?.store_state].filter(Boolean).join(', ');
    if (cityState) storeAddressLines.push(cityState);
    const countryPin = [storeSettings?.store_country, storeSettings?.store_pin].filter(Boolean).join(' - ');
    if (countryPin) storeAddressLines.push(countryPin);

    const itemsHtml = returnItems.map((item, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${item.product_name || item.product_id || '-'}</td>
        <td>${item.variant_sku || 'Base product'}</td>
        <td>${item.size_name || '-'}</td>
        <td>${item.quantity || 0}</td>
        <td>${item.currency || order.currency || 'INR'} ${formatIndianAmount(item.subtotal ?? ((Number(item.price) || 0) * (item.quantity || 0)))}</td>
      </tr>
    `).join('');

    returnSlipWindow.document.write(`
      <html>
        <head>
          <title>Return Slip - ${order.id}</title>
          <style>
            @page { size: 4in 6in; margin: 0.25in; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 0.15in; font-size: 11px; color: #111827; }
            h1 { font-size: 14px; margin: 0 0 8px; }
            h2 { font-size: 11px; margin: 8px 0 4px; text-decoration: underline; }
            .section { border: 1px solid #000; padding: 6px; margin-bottom: 6px; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th, td { border: 1px solid #000; padding: 4px; text-align: left; vertical-align: top; }
            .small { font-size: 10px; }
          </style>
        </head>
        <body>
          <h1>Return Mailing Slip</h1>
          <div class="section">
            <h2>Return From</h2>
            <div>${order.shipping_contact_name || '-'}</div>
            <div>${[order.shipping_contact_isd_code, order.shipping_contact_number].filter(Boolean).join(' ') || '-'}</div>
            <div>${order.shipping_addr_line1 || '-'}</div>
            ${order.shipping_addr_line2 ? `<div>${order.shipping_addr_line2}</div>` : ''}
            <div>${[order.shipping_city, order.shipping_state].filter(Boolean).join(', ') || '-'}</div>
            <div>${[order.shipping_country, order.shipping_pincode].filter(Boolean).join(' - ') || '-'}</div>
          </div>
          <div class="section">
            <h2>Deliver To</h2>
            ${storeAddressLines.map((line) => `<div>${line}</div>`).join('') || '<div>-</div>'}
          </div>
          <div class="section">
            <h2>Items</h2>
            <table>
              <thead>
                <tr><th>#</th><th>Product</th><th>Variant SKU</th><th>Size</th><th>Qty</th><th>Price</th></tr>
              </thead>
              <tbody>${itemsHtml || '<tr><td colspan="6">No items found</td></tr>'}</tbody>
            </table>
          </div>
          <script>window.onload = function() { window.print(); };</script>
        </body>
      </html>
    `);
    returnSlipWindow.document.close();
    returnSlipWindow.focus();
  };

  const handleCapturePickup = async () => {
    if (!order) return;
    if (!pickupDate) {
      setError('Pickup date is required.');
      return;
    }

    if (returnItems.length === 0) {
      setError('No return-requested items found for this order.');
      return;
    }

    if (!Object.values(itemChecks).every(Boolean)) {
      setError('Please confirm all items as picked up.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        pickup_date: new Date(pickupDate).toISOString(),
        pickup_note: pickupNote.trim() || null,
        items: returnItems.map((item) => ({ item_id: item.id, confirmed: true })),
      };

      await axios.post(`/api/orders/courier-access/${token}/pickup`, payload);
      setSuccess('Return pickup captured successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to capture return pickup');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner>
      </Container>
    );
  }

  if (error && !order) {
    return (
      <Container className="py-4" style={{ maxWidth: 1100 }}>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4" style={{ maxWidth: 1100 }}>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="mb-3">
        <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h3 className="mb-1">Courier Return Access</h3>
            <div className="text-muted">Order #{order.id}</div>
            <div className="text-muted small">Return requested at {formatISTDateTime(order.return_requested_at)}</div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-info" onClick={handlePrintReturnSlip}>Print Return Slip</Button>
            <Button variant="warning" onClick={handleCapturePickup} disabled={saving}>
              {saving ? <Spinner size="sm" animation="border" /> : 'Capture Return Pickup'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <h5 className="mb-3">Pickup Details</h5>
          <Form.Group className="mb-3">
            <Form.Label>Date Picked Up</Form.Label>
            <Form.Control type="datetime-local" value={pickupDate} onChange={(event) => setPickupDate(event.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Pickup Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={pickupNote}
              onChange={(event) => setPickupNote(event.target.value)}
              placeholder="Optional pickup note"
            />
          </Form.Group>
          <div>
            <div className="fw-semibold mb-2">Confirm Picked Up Items</div>
            <Table responsive size="sm">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Confirm</th>
                  <th>Item</th>
                  <th>SKU</th>
                  <th>Size</th>
                  <th>Qty</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {returnItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={Boolean(itemChecks[item.id])}
                        onChange={(event) => setItemChecks((current) => ({ ...current, [item.id]: event.target.checked }))}
                      />
                    </td>
                    <td>{item.product_name || item.product_id}</td>
                    <td>{item.variant_sku || 'Base product'}</td>
                    <td>{item.size_name || '-'}</td>
                    <td>{item.quantity}</td>
                    <td>{item.currency} {formatIndianAmount(item.subtotal ?? ((Number(item.price) || 0) * (item.quantity || 0)))}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CourierReturnPage;